import {
  createErrorResource,
  createExternalResource,
  createLoadedResource,
  createLoadingResource,
} from '@next/arable-lib/external-resource';
import { createFeature, createReducer, on } from '@ngrx/store';

import {
  fetchGeneralQuestions,
  fetchGeneralQuestionsSuccess,
  fetchSoilQuestions,
  fetchSoilQuestionsSuccess,
  fetchVarietyPropertiesByEdiCode,
  fetchVarietyPropertiesByEdiCodeFailure,
  fetchVarietyPropertiesByEdiCodeSuccess,
  loadCropFieldsSuccess,
  loadTgaBiomassImage,
  loadTgaBiomassImageFailure,
  loadTgaBiomassImageSuccess,
  loadTgaBiomassPotatoImage,
  loadTgaBiomassPotatoImageFailure,
  loadTgaBiomassPotatoImageSuccess,
  loadTgaSoilImage,
  loadTgaSoilImageFailure,
  loadTgaSoilImageSuccess,
  postGeneralQuestions,
  postSoilQuestions,
  StepperActions,
  fetchGeneralAdviceSuccess,
  fetchGenericSoilQuestionsSuccess,
  fetchGenericSoilQuestions,
  fetchGenericGeneralQuestionsSuccess,
  fetchGenericGeneralQuestions,
  postGenericGeneralQuestions,
  fetchSoilAdviceSuccess,
  postGenericSoilQuestions,
  fetchPlantingQuestions,
  fetchPlantingQuestionsSuccess,
  postPlantingQuestions,
  fetchGenericPlantingQuestions,
  fetchGenericPlantingQuestionsSuccess,
  postGenericPlantingQuestions,
  fetchPlantingAdviceSuccess,
  loadTgaHeightImage,
  loadTgaHeightImageSuccess,
  loadTgaHeightImageFailure,
  loadNotifications,
  loadNotificationsSuccess,
  loadNotificationsFailure,
  fetchCultivationAdviceSuccess,
  loadWaterAuthorityExceedances,
  loadWaterAuthorityExceedancesSuccess,
  loadWaterAuthorityExceedancesFailure,
  closeNotification,
  loadSatelliteImages,
  loadSatelliteImagesSuccess,
  loadSatelliteImagesFailure,
  loadFieldObservationImages,
  loadFieldObservationImagesSuccess,
  loadFieldObservationImagesFailure,
  QuestionnaireActions,
} from './tga.actions';
import { cropYearChanged } from '../../store/actions';
import {
  ImageWithLegendResponse,
  TgaAdviceState,
  TgaQuestionnaireState,
  TgaSteps,
  TgaState,
  VarietyPropertiesTGADto,
  ImageWithLegendAndDescriptionResponse,
  NotificationDto,
  SatelliteImage,
  FieldObservationImage,
} from '../shared/models/models';

export const tgaFeatureKey = 'tga';
export const initialQuestionnaireState: TgaQuestionnaireState = {
  lastGenericStepCompleted: null,
  specificQuestionnaire: {
    cropFieldId: null,
    cropYear: null,
    generalQuestions: null,
    soilQuestions: null,
    plantingQuestions: null,
  },
  genericQuestionnaire: {
    cropYear: null,
    generalQuestions: null,
    soilQuestions: null,
    plantingQuestions: null,
  },
};

export const initialAdviceState: TgaAdviceState = {
  generalAdvice: null,
  soilAdvice: null,
  plantingAdvice: null,
  cultivationAdvice: null,
};

export const initialState: TgaState = {
  cropFields: [],
  questionnaire: initialQuestionnaireState,
  advice: initialAdviceState,
  heightImageData: createExternalResource<ImageWithLegendAndDescriptionResponse>(),
  soilImageData: createExternalResource<ImageWithLegendResponse>(),
  biomassImageData: createExternalResource<ImageWithLegendResponse>(),
  biomassPotatoImageData: createExternalResource<ImageWithLegendAndDescriptionResponse>(),
  satelliteImages: createExternalResource<SatelliteImage[]>(),
  fieldObservationImages: createExternalResource<FieldObservationImage[]>(),
  stepper: {
    activeStep: TgaSteps.General,
  },
  varietyProperties: createExternalResource<VarietyPropertiesTGADto>(),
  waterAuthority: {
    exceedances: null,
    loading: false,
  },
  notifications: createExternalResource<NotificationDto[]>(),
  dismissedNotifications: [],
};

export const reducer = createReducer(
  initialState,
  on(loadTgaSoilImage, (state) => ({
    ...state,
    soilImageData: createLoadingResource(),
  })),

  on(loadTgaSoilImageSuccess, (state, action) => ({
    ...state,
    soilImageData: createLoadedResource(action.payload),
  })),

  on(loadTgaSoilImageFailure, (state) => ({
    ...state,
    soilImageData: createErrorResource(),
  })),

  on(loadTgaBiomassImage, (state) => ({
    ...state,
    biomassImageData: createLoadingResource(),
  })),

  on(loadTgaBiomassImageSuccess, (state, action) => ({
    ...state,
    biomassImageData: createLoadedResource(action.payload),
  })),

  on(loadTgaBiomassImageFailure, (state) => ({
    ...state,
    biomassImageData: createErrorResource(),
  })),

  on(loadTgaBiomassPotatoImage, (state) => ({
    ...state,
    biomassPotatoImageData: createLoadingResource(),
  })),

  on(loadTgaBiomassPotatoImageSuccess, (state, action) => ({
    ...state,
    biomassPotatoImageData: createLoadedResource(action.payload),
  })),

  on(loadTgaBiomassPotatoImageFailure, (state) => ({
    ...state,
    biomassPotatoImageData: createErrorResource(),
  })),

  on(fetchVarietyPropertiesByEdiCode, (state) => ({
    ...state,
    varietyProperties: createLoadingResource(),
  })),

  on(fetchVarietyPropertiesByEdiCodeSuccess, (state, action) => ({
    ...state,
    varietyProperties: createLoadedResource(action.payload),
  })),

  on(fetchVarietyPropertiesByEdiCodeFailure, (state) => ({
    ...state,
    varietyProperties: createErrorResource(),
  })),
  on(QuestionnaireActions.setSpecificQuestionnaireContext, (state, { cropFieldId, cropYear }) => ({
    ...state,
    questionnaire: {
      ...state.questionnaire,
      specificQuestionnaire: {
        ...state.questionnaire.specificQuestionnaire,
        cropFieldId,
        cropYear,
      },
    },
  })),
  on(fetchGeneralQuestions, (state, { payload }) => ({
    ...state,
    questionnaire: {
      ...state.questionnaire,
      specificQuestionnaire: {
        ...state.questionnaire.specificQuestionnaire,
        cropFieldId: payload.cropFieldId,
        cropYear: payload.cropYear,
      },
    },
  })),
  on(fetchGeneralQuestionsSuccess, (state, { payload }) => ({
    ...state,
    questionnaire: {
      ...state.questionnaire,
      specificQuestionnaire: {
        ...state.questionnaire.specificQuestionnaire,
        generalQuestions: payload.questions,
      },
    },
  })),
  on(postGeneralQuestions, (state, { payload }) => {
    const updatedCropFields = state.cropFields.map((field) =>
      field.cropField.id === payload.answers.cropFieldId
        ? {
          ...field,
          cropField: {
            ...field.cropField,
            ...(payload.answers.cropVarietyId && {
              cropVarietyId: payload.answers.cropVarietyId,
            }),
          },
          questionnaireStatus: {
            ...field.questionnaireStatus,
            lastSpecificStepCompleted: TgaSteps.General,
          },
        }
        : field,
    );
    return {
      ...state,
      cropFields: updatedCropFields,
      questionnaire: {
        ...state.questionnaire,
        specificQuestionnaire: {
          ...state.questionnaire.specificQuestionnaire,
          generalQuestions: payload.answers,
        },
      },
      advice: { generalAdvice: null, soilAdvice: null, plantingAdvice: null, cultivationAdvice: null },
    };
  }),


  on(fetchSoilQuestions, (state, { payload }) => ({
    ...state,
    questionnaire: {
      ...state.questionnaire,
      specificQuestionnaire: {
        ...state.questionnaire.specificQuestionnaire,
        cropFieldId: payload.cropFieldId,
        cropYear: payload.cropYear,
      },
    },
  })),
  on(fetchSoilQuestionsSuccess, (state, { payload }) => ({
    ...state,
    questionnaire: {
      ...state.questionnaire,
      specificQuestionnaire: {
        ...state.questionnaire.specificQuestionnaire,
        soilQuestions: payload.questions,
      },
    },
  })),
  on(postSoilQuestions, (state, { payload }) => {
    const updatedCropFields = state.cropFields.map((field) =>
      field.cropField.id === payload.answers.cropFieldId
        ? {
          ...field,
          questionnaireStatus: {
            ...field.questionnaireStatus,
            lastSpecificStepCompleted: field.questionnaireStatus.lastSpecificStepCompleted === TgaSteps.General
              ? TgaSteps.Soil
              : field.questionnaireStatus.lastSpecificStepCompleted,
          },
        }
        : field,
    );
    return {
      ...state,
      cropFields: updatedCropFields,
      questionnaire: {
        ...state.questionnaire,
        specificQuestionnaire: {
          ...state.questionnaire.specificQuestionnaire,
          soilQuestions: payload.answers,
        },
      },
      advice: { generalAdvice: null, soilAdvice: null, plantingAdvice: null, cultivationAdvice: null },
    };
  }),


  on(fetchPlantingQuestions, (state, { payload }) => ({
    ...state,
    questionnaire: {
      ...state.questionnaire,
      specificQuestionnaire: {
        ...state.questionnaire.specificQuestionnaire,
        cropFieldId: payload.cropFieldId,
        cropYear: payload.cropYear,
      },
    },
  })),
  on(fetchPlantingQuestionsSuccess, (state, { payload }) => ({
    ...state,
    questionnaire: {
      ...state.questionnaire,
      specificQuestionnaire: {
        ...state.questionnaire.specificQuestionnaire,
        plantingQuestions: payload.questions,
      },
    },
  })),
  on(postPlantingQuestions, (state, { payload }) => {
    const updatedCropFields = state.cropFields.map((field) =>
      field.cropField.id === payload.answers.cropFieldId
        ? {
          ...field,
          questionnaireStatus: {
            ...field.questionnaireStatus,
            lastSpecificStepCompleted:
              (field.questionnaireStatus.lastSpecificStepCompleted !== TgaSteps.Growing &&
                field.questionnaireStatus.lastSpecificStepCompleted !== TgaSteps.Evaluation)
                ? TgaSteps.Planting
                : field.questionnaireStatus.lastSpecificStepCompleted,
          },
        }
        : field,
    );
    return {
      ...state,
      cropFields: updatedCropFields,
      questionnaire: {
        ...state.questionnaire,
        specificQuestionnaire: {
          ...state.questionnaire.specificQuestionnaire,
          plantingQuestions: payload.answers,
        },
      },
    };
  }),


  on(fetchGenericGeneralQuestions, (state, { payload }) => ({
    ...state,
    questionnaire: {
      ...state.questionnaire,
      genericQuestionnaire: {
        ...state.questionnaire.genericQuestionnaire,
        cropYear: payload.cropYear,
      },
    },
  })),

  on(fetchGenericGeneralQuestionsSuccess, (state, { payload }) => ({
    ...state,
    questionnaire: {
      ...state.questionnaire,
      genericQuestionnaire: {
        ...state.questionnaire.genericQuestionnaire,
        generalQuestions: payload.questions,
      },
    },
  })),


  on(postGenericGeneralQuestions, (state, { payload }) => {
    const newLastStep = state.questionnaire.lastGenericStepCompleted ?? TgaSteps.General;
    const { cropYear, ...genericGeneral } = payload.answers;
    return {
      ...state,
      questionnaire: {
        ...state.questionnaire,
        lastGenericStepCompleted: newLastStep,
        genericQuestionnaire: {
          ...state.questionnaire.genericQuestionnaire,
          cropYear,
          generalQuestions: {
            hasFilledInSpecific: state.questionnaire.genericQuestionnaire.generalQuestions?.hasFilledInSpecific || false,
            genericGeneral,
          },
        },
      },
    };
  }),


  on(loadCropFieldsSuccess, (state, { payload }) => ({
    ...state,
    cropFields: payload.cropFields,
    questionnaire: {
      ...state.questionnaire,
      lastGenericStepCompleted: payload.cropFields[0]?.questionnaireStatus.lastGenericStepCompleted || null,
    },
  })),

  on(fetchGeneralAdviceSuccess, (state, { payload }) => ({
    ...state,
    advice: {
      ...state.advice,
      generalAdvice: {
        data: payload.generalAdvice,
        cropFieldId: payload.cropFieldId,
        cropYear: payload.cropYear,
        translationParams: payload.translationParams,
      },
    },
  })),

  on(fetchSoilAdviceSuccess, (state, { payload }) => ({
    ...state,
    advice: {
      ...state.advice,
      soilAdvice: {
        data: payload.soilAdvice,
        cropFieldId: payload.cropFieldId,
        cropYear: payload.cropYear,
        translationParams: payload.translationParams,
      },
    },
  })),

  on(StepperActions.updateActiveStep, (state, { stepName }) => {
    return {
      ...state,
      stepper: {
        ...state.stepper,
        activeStep: stepName || state.stepper.activeStep,
      },
    };
  }),

  on(StepperActions.setActiveStepByKey, (state, { stepKey }) => ({
    ...state,
    stepper: {
      ...state.stepper,
      activeStep: stepKey,
    },
  })),

  on(StepperActions.moveToNextStep, (state, { nextStep }) => {
    if (nextStep) {
      return {
        ...state,
        stepper: {
          ...state.stepper,
          activeStep: nextStep,
        },
      };
    }
    return state;
  }),

  on(fetchGenericSoilQuestions, (state, { payload }) => ({
    ...state,
    questionnaire: {
      ...state.questionnaire,
      genericQuestionnaire: {
        ...state.questionnaire.genericQuestionnaire,
        cropYear: payload.cropYear,
      },
    },
  })),

  on(fetchGenericSoilQuestionsSuccess, (state, { payload }) => ({
    ...state,
    questionnaire: {
      ...state.questionnaire,
      genericQuestionnaire: {
        ...state.questionnaire.genericQuestionnaire,
        soilQuestions: payload.questions,
      },
    },
  })),

  on(postGenericSoilQuestions, (state, { payload }) => {
    const newLastStep =
      (state.questionnaire.lastGenericStepCompleted === TgaSteps.General || !state.questionnaire.lastGenericStepCompleted)
        ? TgaSteps.Soil : state.questionnaire.lastGenericStepCompleted;
    const { cropYear, ...genericSoil } = payload.answers;
    return {
      ...state,
      questionnaire: {
        ...state.questionnaire,
        genericQuestionnaire: {
          ...state.questionnaire.genericQuestionnaire,
          cropYear,
          soilQuestions: {
            hasFilledInSpecific: state.questionnaire.genericQuestionnaire.soilQuestions?.hasFilledInSpecific || false,
            genericSoil,
          },
        },
        lastGenericStepCompleted: newLastStep,
      },
      advice: { ...state.advice, soilAdvice: null },
    };
  }),

  on(fetchGenericPlantingQuestions, (state, { payload }) => ({
    ...state,
    questionnaire: {
      ...state.questionnaire,
      genericQuestionnaire: {
        ...state.questionnaire.genericQuestionnaire,
        cropYear: payload.cropYear,
      },
    },
  })),

  on(fetchGenericPlantingQuestionsSuccess, (state, { payload }) => ({
    ...state,
    questionnaire: {
      ...state.questionnaire,
      genericQuestionnaire: {
        ...state.questionnaire.genericQuestionnaire,
        plantingQuestions: payload.questions,
      },
    },
  })),

  on(postGenericPlantingQuestions, (state, { payload }) => {
    const newLastStep =
      (state.questionnaire.lastGenericStepCompleted === TgaSteps.General ||
        state.questionnaire.lastGenericStepCompleted === TgaSteps.Soil ||
        !state.questionnaire.lastGenericStepCompleted)
        ? TgaSteps.Planting : state.questionnaire.lastGenericStepCompleted;
    const { cropYear, ...genericPlanting } = payload.answers;
    return {
      ...state,
      questionnaire: {
        ...state.questionnaire,
        genericQuestionnaire: {
          ...state.questionnaire.genericQuestionnaire,
          cropYear,
          plantingQuestions: {
            hasFilledInSpecific: state.questionnaire.genericQuestionnaire.plantingQuestions?.hasFilledInSpecific || false,
            genericPlanting,
          },
        },
        lastGenericStepCompleted: newLastStep,
      },
      advice: { ...state.advice, plantingAdvice: null },
    };
  }),


  on(fetchPlantingAdviceSuccess, (state, { payload }) => ({
    ...state,
    advice: {
      ...state.advice,
      plantingAdvice: {
        data: payload.plantingAdvice,
        cropFieldId: payload.cropFieldId,
        cropYear: payload.cropYear,
        translationParams: payload.translationParams,
      },
    },
  })),

  on(loadTgaHeightImage, (state) => ({
    ...state,
    heightImageData: createLoadingResource(),
  })),

  on(loadTgaHeightImageSuccess, (state, action) => ({
    ...state,
    heightImageData: createLoadedResource(action.payload),
  })),

  on(loadTgaHeightImageFailure, (state) => ({
    ...state,
    heightImageData: createErrorResource(),
  })),

  on(loadSatelliteImages, (state) => ({
    ...state,
    satelliteImages: createLoadingResource(),
  })),

  on(loadSatelliteImagesSuccess, (state, action) => ({
    ...state,
    satelliteImages: createLoadedResource(action.payload),
  })),

  on(loadSatelliteImagesFailure, (state) => ({
    ...state,
    satelliteImages: createErrorResource(),
  })),

  on(loadFieldObservationImages, (state) => ({
    ...state,
    fieldObservationImages: createLoadingResource(),
  })),

  on(loadFieldObservationImagesSuccess, (state, action) => ({
    ...state,
    fieldObservationImages: createLoadedResource(action.payload),
  })),

  on(loadFieldObservationImagesFailure, (state) => ({
    ...state,
    fieldObservationImages: createErrorResource(),
  })),

  on(loadNotifications, (state) => ({
    ...state,
    notifications: createLoadingResource(),
  })),

  on(loadNotificationsSuccess, (state, { payload }) => {
    const openNotifications = payload.notifications.filter((n) => !n.isClosed);
    const closedNotifications = payload.notifications.filter((n) => n.isClosed);

    return {
      ...state,
      notifications: createLoadedResource(openNotifications),
      dismissedNotifications: closedNotifications,
    };
  }),

  on(loadNotificationsFailure, (state) => ({
    ...state,
    notifications: createErrorResource(),
  })),

  on(closeNotification, (state, { payload: { notificationId } }) => {

    if (state.notifications.state !== 'loaded') {
      return state;
    }
    const notification = state.notifications.data.find((n) => n.id === notificationId);
    if (!notification) {
      return state;
    }
    const dismissedNotification: NotificationDto = {
      ...notification,
      isClosed: true,
    };

    return {
      ...state,
      notifications: createLoadedResource(
        state.notifications.data.filter((n: NotificationDto) => n !== notification),
      ),
      dismissedNotifications: [dismissedNotification, ...state.dismissedNotifications].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      ),
    };
  }),

  on(fetchCultivationAdviceSuccess, (state, { payload }) => ({
    ...state,
    advice: {
      ...state.advice,
      cultivationAdvice: {
        data: payload.cultivationAdvice,
        cropFieldId: payload.cropFieldId,
        cropYear: payload.cropYear,
        translationParams: payload.translationParams,
      },
    },
  })),

  on(loadWaterAuthorityExceedances, (state) => ({
    ...state,
    waterAuthority: {
      ...state.waterAuthority,
      loading: true,
    },
  })),

  on(loadWaterAuthorityExceedancesSuccess, (state, { payload }) => ({
    ...state,
    waterAuthority: {
      ...state.waterAuthority,
      loading: false,
      exceedances: payload.results,
    },
  })),

  on(loadWaterAuthorityExceedancesFailure, (state) => ({
    ...state,
    waterAuthority: {
      ...state.waterAuthority,
      loading: false,
    },
  })),

  on(cropYearChanged, (state) => ({
    ...state,
    cropFields: [],
    questionnaire: initialQuestionnaireState,
    advice: initialAdviceState,
    heightImageData: createExternalResource<ImageWithLegendAndDescriptionResponse>(),
    soilImageData: createExternalResource<ImageWithLegendResponse>(),
    biomassImageData: createExternalResource<ImageWithLegendResponse>(),
    biomassPotatoImageData: createExternalResource<ImageWithLegendAndDescriptionResponse>(),
    satelliteImages: createExternalResource<SatelliteImage[]>(),
    fieldObservationImages: createExternalResource<FieldObservationImage[]>(),
    stepper: { activeStep: TgaSteps.General },
    waterAuthority: { exceedances: null, loading: false },
    notifications: createExternalResource<NotificationDto[]>(),
    dismissedNotifications: [],
  })),
);

export const tgaFeature = createFeature({
  name: tgaFeatureKey,
  reducer,
});
export const { selectHeightImageData, selectSoilImageData, selectVarietyProperties } = tgaFeature;

export const selectTgaState = tgaFeature.selectTgaState;
